/** Shared helpers for product variant options (Color, Size, …) */

export function isColorOptionName(name = "") {
  return /color|colour/i.test(String(name).trim());
}

/** Normalize API/DB values to { label, color }[] */
export function normalizeOptionValues(values = []) {
  return (values || [])
    .map((v) => {
      if (typeof v === "string") {
        const label = v.trim();
        return label ? { label, color: "" } : null;
      }
      if (v && typeof v === "object") {
        const label = String(v.label || v.value || v.name || "").trim();
        if (!label) return null;
        return {
          label,
          color: String(v.color || "").trim(),
        };
      }
      return null;
    })
    .filter(Boolean);
}

export function optionValueLabel(val) {
  if (typeof val === "string") return val;
  return val?.label || "";
}

export function optionValueColor(val) {
  if (typeof val === "string") return "";
  return val?.color || "";
}

/** Guess a hex when admin only typed a common color name */
const NAMED_COLORS = {
  black: "#1a1a1a",
  white: "#f5f5f5",
  red: "#c0392b",
  blue: "#2980b9",
  green: "#27ae60",
  yellow: "#f1c40f",
  orange: "#e67e22",
  purple: "#8e44ad",
  pink: "#e84393",
  grey: "#7f8c8d",
  gray: "#7f8c8d",
  brown: "#6e4b3a",
  beige: "#d7ccc8",
  navy: "#1a237e",
  cream: "#fff8e7",
};

export function guessColorFromLabel(label = "") {
  const key = String(label).trim().toLowerCase();
  if (NAMED_COLORS[key]) return NAMED_COLORS[key];
  for (const [name, hex] of Object.entries(NAMED_COLORS)) {
    if (key.includes(name)) return hex;
  }
  return "#888888";
}

export function findMatchingVariant(variants, selected = {}) {
  if (!variants?.length) return null;
  return (
    variants.find((v) =>
      Object.entries(selected).every(([k, val]) => v.attributes?.[k] === val)
    ) || null
  );
}

/** Default variant + first option values for quick add-to-cart from listings */
export function getDefaultCartExtras(product) {
  if (!product?.variantOptions?.length && !product?.variants?.length) {
    return {};
  }

  const defaultVariant =
    product.variants?.find((v) => v.isDefault) || product.variants?.[0] || null;

  const selectedAttributes = {};
  (product.variantOptions || []).forEach((opt) => {
    const firstLabel = optionValueLabel(opt.values?.[0]);
    selectedAttributes[opt.name] =
      defaultVariant?.attributes?.[opt.name] || firstLabel || "";
  });

  const matched =
    findMatchingVariant(product.variants, selectedAttributes) || defaultVariant;

  return {
    variantId: matched?.id || null,
    selectedAttributes,
  };
}

export function getDefaultVariant(product) {
  if (!product?.variants?.length) return null;
  const { variantId } = getDefaultCartExtras(product);
  if (variantId) {
    return (
      product.variants.find((v) => String(v.id) === String(variantId)) || null
    );
  }
  return product.variants.find((v) => v.isDefault) || product.variants[0] || null;
}

/** Stock available for listing cards / filters (any variant, else base stock). */
export function getListingStock(product) {
  if (!product) return 0;
  const variants = product.variants || [];
  if (variants.length > 0) {
    return variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
  }
  return Number(product.stock) || 0;
}
